import HttpClientError from '@errors/HttpClientError';
import axios, {
  type AxiosInstance,
  type AxiosResponse,
  isAxiosError,
} from 'axios';

import interceptors from './interceptorsBackendClient';

class BackendHttpClient {
  private readonly instance: AxiosInstance;

  constructor() {
    this.instance = axios.create({
      baseURL: import.meta.env.VITE_MAIN_BACKEND_URL,
      timeout: 10000,
    });
    interceptors.setToken(this.instance);
    interceptors.logout(this.instance);
  }

  public async get<T = any, S = unknown>({
    headers = {},
    params,
    path,
  }: {
    headers?: Record<string, string>;
    params?: S;
    path: string;
  }): Promise<AxiosResponse<T>> {
    try {
      const response = await this.instance.get<T>(path, {
        params,
        headers: {
          ...headers,
        },
      });
      return response;
    } catch (err) {
      if (isAxiosError(err)) {
        throw new HttpClientError({
          body: err.response?.data,
          message: `${err.response?.status} = ${err.response?.statusText}`,
          response: err.response,
        });
      }
      throw new Error('Erro não tratado');
    }
  }

  public async post<T, S = unknown>({
    body,
    path,
  }: {
    body?: S;
    path: string;
  }): Promise<AxiosResponse<T>> {
    try {
      const response = await this.instance.post<T>(path, body);
      return response;
    } catch (err) {
      if (isAxiosError(err)) {
        throw new HttpClientError({
          body: err.response?.data,
          message: `${err.response?.status} = ${err.response?.statusText}`,
          response: err.response,
        });
      }
      if (err instanceof Error) {
        throw new Error(err.message);
      }
      throw new Error('Erro não tratado');
    }
  }

  public async put<T, S = unknown, R = unknown>({
    body,
    params,
    path,
  }: {
    body?: S;
    params?: R;
    path: string;
  }): Promise<AxiosResponse<T>> {
    try {
      const response = await this.instance.put<T>(path, body, {
        params,
      });
      return response;
    } catch (err) {
      if (isAxiosError(err)) {
        throw new HttpClientError({
          body: err.response?.data,
          message: `${err.response?.status} = ${err.response?.statusText}`,
          response: err.response,
        });
      }
      throw new Error('Erro não tratado');
    }
  }

  public async delete<T, S = unknown>({
    params,
    path,
  }: {
    params?: S;
    path: string;
  }): Promise<AxiosResponse<T>> {
    try {
      const response = await this.instance.delete<T>(path, {
        params,
      });
      return response;
    } catch (err) {
      if (isAxiosError(err)) {
        throw new HttpClientError({
          body: err.response?.data,
          message: `${err.response?.status} = ${err.response?.statusText}`,
          response: err.response,
        });
      }
      throw new Error('Erro não tratado');
    }
  }

  public removeBearerToken(): void {
    this.instance.defaults.headers.common.Authorization = '';
  }

  public setBearerToken(token: string): void {
    this.instance.defaults.headers.common.Authorization = `Bearer ${token}`;
  }
}

export default new BackendHttpClient();
